import React, { useEffect, useMemo, useState } from "react";
import LayoutDashboard from "layouts/layout-dashboard-collapse";
import {
  IconAlignCenter,
  IconAlignLeft,
  IconAlignRight,
  IconArrowDown,
  IconArrowUp,
  IconBold,
  IconDeviceFloppy,
  IconPhotoUp,
  IconPrinter,
  IconRefresh,
} from "@tabler/icons-react";
import { KUBER_LOGO_BASE64 } from "constants/kuber-logo-base64";
import {
  DEFAULT_PRINT_LAYOUT_SETTINGS,
  getStoredPrintLayoutSettings,
  getWrapperPaperWidth,
  savePrintLayoutSettings,
} from "./utils/printLayoutSettings";
import { printBookingPOS } from "./utils/BookingPOSPrinter";
import {
  editorStateToPrintLayoutPayload,
  printLayoutToEditorState,
  resolvePrintLayout,
  savePrintLayoutRemote,
  uploadPrintLayoutLogo,
} from "./utils/printLayoutApi";

const DEFAULT_FIELDS = [
  { id: "host", label: "HOST", value: "GEORGIANA 24", visible: true, bold: true, align: "left", fontSize: 12 },
  { id: "code", label: "CODE", value: "KBA054318", visible: true, bold: true, align: "right", fontSize: 12 },
  { id: "date", label: "DATE", value: "2026-09-15", visible: true, bold: true, align: "right", fontSize: 12 },
  { id: "print_date", label: "PRINT DATE", value: "15 Sep 2026", visible: true, bold: true, align: "right", fontSize: 12 },
  { id: "print_time", label: "PRINT TIME", value: "16.51", visible: true, bold: true, align: "right", fontSize: 12 },
  { id: "guest", label: "GUEST", value: "uno flip", visible: true, bold: true, align: "right", fontSize: 12 },
  { id: "hotel", label: "HOTEL", value: "Ubud hotel", visible: true, bold: true, align: "right", fontSize: 12 },
  { id: "agent", label: "AGENT", value: "BINTANG BISMA/J", visible: true, bold: true, align: "right", fontSize: 12 },
  { id: "driver", label: "DRIVER", value: "Pkk drive", visible: true, bold: true, align: "right", fontSize: 12 },
  { id: "transport", label: "TRANSPORT", value: "OT", visible: true, bold: true, align: "right", fontSize: 12 },
  { id: "total_pax", label: "TOTAL PAX", value: "2", visible: true, bold: true, align: "right", fontSize: 12 },
];

const PRINT_TYPES = [
  { value: "invoice", label: "Guest Invoice" },
  { value: "ticket", label: "Ticket" },
  { value: "meal", label: "Meal Voucher" },
];

const PREVIEW_FONT_FAMILIES = {
  font_a: "'Courier New', monospace",
  font_b: "'Arial Narrow', Arial, sans-serif",
  font_c: "'Roboto Mono', 'Courier New', monospace",
};

const apiPrintType = (value) => (value === "invoice" ? "guest_invoice" : value);

// Printer thermal hanya punya 3 kelas ukuran (lihat commandSizeFor di BookingPOSPrinter).
const PRINTER_SIZE_OPTIONS = [
  { value: 8, label: "Kecil (Font B)" },
  { value: 12, label: "Normal" },
  { value: 16, label: "Besar (2x, setengah lebar)" },
];

const toPrinterSizeValue = (fontSize) => {
  const size = Number(fontSize);
  if (size >= 15) return 16;
  if (size < 10) return 8;
  return 12;
};

// Title & footer di printer tidak ikut line height / character spacing detail.
const FIXED_PREVIEW_TEXT = {
  letterSpacing: 0,
  lineHeight: DEFAULT_PRINT_LAYOUT_SETTINGS.lineHeight,
};

const PRINTER_SIZE_FIELDS = [
  {
    key: "headerFontSize",
    label: "Header Font",
    hint: "Ukuran nama perusahaan di bagian atas struk.",
  },
  {
    key: "companyFontSize",
    label: "Company Detail Font",
    hint: "Ukuran alamat perusahaan di bawah nama.",
  },
  {
    key: "titleFontSize",
    label: "Title Font",
    hint: "Besar: PREMIUM/REGULAR besar. Normal: PREMIUM normal. Kecil: PREMIUM, TICKET, Meals Voucher & label copy ikut kecil.",
  },
  {
    key: "footerFontSize",
    label: "Footer Font",
    hint: "Ukuran teks footer di bagian bawah struk.",
  },
];

const DEFAULT_ITEM_STYLES = [
  { id: "itemHeader", label: "Activity Header", sample: "Activity                                      Qty", visible: true, bold: true, align: "left", fontSize: 11 },
  { id: "itemName", label: "Activity Name", sample: "ATV Single", visible: true, bold: true, align: "left", fontSize: 14 },
  { id: "itemQty", label: "Qty", sample: "2", visible: true, bold: true, align: "right", fontSize: 14 },
  { id: "mealName", label: "Meal Item", sample: "Lunch Package", visible: true, bold: true, align: "left", fontSize: 14 },
  { id: "mealQty", label: "Meal Qty", sample: "2", visible: true, bold: true, align: "right", fontSize: 14 },
  { id: "totalPaid", label: "Total Paid", sample: "Rp. 2.884.000", visible: true, bold: true, align: "right", fontSize: 12 },
];

const moveItem = (items, index, direction) => {
  const targetIndex = direction === "up" ? index - 1 : index + 1;
  if (targetIndex < 0 || targetIndex >= items.length) return items;
  const nextItems = [...items];
  const [item] = nextItems.splice(index, 1);
  nextItems.splice(targetIndex, 0, item);
  return nextItems;
};

const alignIcon = {
  left: IconAlignLeft,
  center: IconAlignCenter,
  right: IconAlignRight,
};

const PrintLayoutSettingsPage = () => {
  const [printType, setPrintType] = useState("ticket");
  const [settings, setSettings] = useState(() => getStoredPrintLayoutSettings());
  const [companyName, setCompanyName] = useState(() => getStoredPrintLayoutSettings().companyName);
  const [companyAddress, setCompanyAddress] = useState(() => getStoredPrintLayoutSettings().companyAddress);
  const [footerText, setFooterText] = useState(() => getStoredPrintLayoutSettings().footerText);
  const [logoPreview, setLogoPreview] = useState(() => getStoredPrintLayoutSettings().logoDataUrl || KUBER_LOGO_BASE64);
  const [logoFile, setLogoFile] = useState(null);
  const [fields, setFields] = useState(() => getStoredPrintLayoutSettings().fields || DEFAULT_FIELDS);
  const [itemStyles, setItemStyles] = useState(() => getStoredPrintLayoutSettings().itemStyles || DEFAULT_ITEM_STYLES);
  const [savedAt, setSavedAt] = useState("");
  const [remoteLoading, setRemoteLoading] = useState(false);
  const [remoteError, setRemoteError] = useState("");
  const [wrapperPaperWidth] = useState(() => getWrapperPaperWidth());

  useEffect(() => {
    let cancelled = false;
    const loadLayout = async () => {
      setRemoteLoading(true);
      setRemoteError("");
      try {
        const layout = await resolvePrintLayout(apiPrintType(printType));
        if (cancelled) return;
        if (!layout) {
          setSettings((current) => ({ ...current, id: undefined, print_type: apiPrintType(printType) }));
          return;
        }
        const mapped = printLayoutToEditorState(layout);
        setSettings((current) => ({ ...current, ...mapped, id: layout.id, print_type: layout.print_type }));
        if (mapped.companyName) setCompanyName(mapped.companyName);
        if (mapped.companyAddress) setCompanyAddress(mapped.companyAddress);
        if (mapped.footerText !== undefined) setFooterText(mapped.footerText);
        if (mapped.logoDataUrl) setLogoPreview(mapped.logoDataUrl);
        if (Array.isArray(mapped.fields)) setFields(mapped.fields);
        if (Array.isArray(mapped.itemStyles)) setItemStyles(mapped.itemStyles);
        savePrintLayoutSettings({ ...getStoredPrintLayoutSettings(), ...mapped });
      } catch (error) {
        if (!cancelled) setRemoteError(error.message || "Layout remote tidak dapat dimuat");
      } finally {
        if (!cancelled) setRemoteLoading(false);
      }
    };
    loadLayout();
    return () => { cancelled = true; };
  }, [printType]);

  const visibleFields = useMemo(
    () => fields.filter((field) => field.visible),
    [fields]
  );

  const updateField = (id, updates) => {
    setFields((current) =>
      current.map((field) => (field.id === id ? { ...field, ...updates } : field))
    );
  };

  const updateItemStyle = (id, updates) => {
    setItemStyles((current) =>
      current.map((item) => (item.id === id ? { ...item, ...updates } : item))
    );
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setLogoFile(file);

    const reader = new FileReader();
    reader.onload = () => setLogoPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const resetDefaults = () => {
    setPrintType("ticket");
    setCompanyName(DEFAULT_PRINT_LAYOUT_SETTINGS.companyName);
    setCompanyAddress(DEFAULT_PRINT_LAYOUT_SETTINGS.companyAddress);
    setFooterText(DEFAULT_PRINT_LAYOUT_SETTINGS.footerText);
    setLogoPreview(KUBER_LOGO_BASE64);
    setSettings(DEFAULT_PRINT_LAYOUT_SETTINGS);
    setFields(DEFAULT_FIELDS);
    setItemStyles(DEFAULT_ITEM_STYLES);
    savePrintLayoutSettings({
      ...DEFAULT_PRINT_LAYOUT_SETTINGS,
      fields: DEFAULT_FIELDS,
      itemStyles: DEFAULT_ITEM_STYLES,
    });
    setSavedAt("Default restored");
  };

  const handleSaveSettings = async () => {
    const localSettings = {
      ...settings,
      companyName,
      companyAddress,
      footerText,
      logoDataUrl: logoPreview === KUBER_LOGO_BASE64 ? "" : logoPreview,
      fields,
      itemStyles,
      print_type: apiPrintType(printType),
    };
    try {
      const response = await savePrintLayoutRemote(
        settings.id,
        editorStateToPrintLayoutPayload({ settings: localSettings, companyName, companyAddress, footerText, fields, itemStyles })
      );
      let serverLayout = response.data;
      if (logoFile && serverLayout?.id) {
        serverLayout = (await uploadPrintLayoutLogo(serverLayout.id, logoFile)).data;
        setLogoFile(null);
      }
      const savedSettings = savePrintLayoutSettings({
        ...localSettings,
        ...printLayoutToEditorState(serverLayout),
        id: serverLayout?.id || settings.id,
      });
      setSettings(savedSettings);
      setSavedAt("Saved to server");
      setRemoteError("");
    } catch (error) {
      setRemoteError(error.message || "Gagal menyimpan layout ke server");
    }
  };

  const handleTestPrint = () => {
    const savedSettings = savePrintLayoutSettings({
      ...settings,
      companyName,
      companyAddress,
      footerText,
      logoDataUrl: logoPreview === KUBER_LOGO_BASE64 ? "" : logoPreview,
      fields,
      itemStyles,
      print_type: apiPrintType(printType),
    });
    setSettings(savedSettings);
    // Test Print memakai isi editor saat ini, bukan layout API yang tersimpan.
    printBookingPOS(
      {
        code: "TEST-PRINT",
        reservation_date: new Date().toISOString().slice(0, 10),
        guest_name: "Test Guest",
        guest_pax: 2,
        guest_transport: "Own Transport",
        total_price: 1400000,
        tax: 0,
        payment_method: { name: "TEST" },
        customer: { name: "Test Guest", nationality: "-" },
        agent: { name: "-" },
        activities: [{ activity: "ATV Single", qty: 2, price: 700000 }],
        trip: { name: "Trip 1", start_time: "08:30", end_time: "09:00" },
        track: "A",
      },
      {
        type: printType === "invoice" ? "invoice" : "ticket",
        user: { name: "Test Host" },
        activities: [{ activity: "ATV Single", qty: 2, price: 700000 }],
        meals: [{ name: "Test Meal", qty: 2 }],
        printTickets: { activity: printType !== "meal", meal: true },
        layoutSettings: savedSettings,
      }
    );
    setSavedAt("Test print sent");
  };

  const itemStyleMap = useMemo(
    () =>
      itemStyles.reduce((result, item) => {
        result[item.id] = item;
        return result;
      }, {}),
    [itemStyles]
  );

  return (
    <LayoutDashboard>
      <div className="container-fluid py-3">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
          <div>
            <h1 className="h2 mb-1">Print Layout Settings</h1>
            <div className="text-muted">
              Adjustment tipis untuk layout print Booking V4 yang sedang dipakai.
            </div>
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-outline-secondary" onClick={resetDefaults}>
              <IconRefresh size={16} className="me-1" />
              Reset
            </button>
            <button className="btn btn-primary" type="button" onClick={handleSaveSettings} disabled={remoteLoading}>
              <IconDeviceFloppy size={16} className="me-1" />
              Save Settings
            </button>
            <button className="btn btn-outline-primary" type="button" onClick={handleTestPrint}>
              <IconPrinter size={16} className="me-1" />
              Test Print
            </button>
          </div>
        </div>
        {remoteLoading && <div className="alert alert-info py-2 mb-3">Memuat layout print dari server...</div>}
        {remoteError && <div className="alert alert-warning py-2 mb-3">{remoteError}</div>}
        {savedAt && (
          <div className="alert alert-success py-2 mb-3">
            Setting print V4 tersimpan di device ini. Cetak berikutnya akan memakai setting ini.
            <strong className="ms-1">{savedAt}</strong>
          </div>
        )}

        <div className="row g-3">
          <div className="col-xl-8">
            <div className="card mb-3">
              <div className="card-header py-2">
                <h3 className="card-title mb-0">Header, Logo & Footer</h3>
              </div>
              <div className="card-body py-3">
                <div className="row g-2">
                  <div className="col-md-4">
                    <label className="form-label">Print Type</label>
                    <select
                      className="form-select"
                      value={printType}
                      onChange={(event) => setPrintType(event.target.value)}
                    >
                      {PRINT_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Company Name</label>
                    <input
                      className="form-control"
                      value={companyName}
                      onChange={(event) => setCompanyName(event.target.value)}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Upload Logo</label>
                    <label className="btn btn-outline-primary w-100">
                      <IconPhotoUp size={16} className="me-1" />
                      Choose Logo
                      <input type="file" accept="image/*" className="d-none" onChange={handleLogoUpload} />
                    </label>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Company Address</label>
                    <textarea
                      className="form-control"
                      rows={2}
                      value={companyAddress}
                      onChange={(event) => setCompanyAddress(event.target.value)}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Footer Text</label>
                    <textarea
                      className="form-control"
                      rows={2}
                      value={footerText}
                      onChange={(event) => setFooterText(event.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="card mb-3">
              <div className="card-header py-2">
                <h3 className="card-title mb-0">Ukuran Dasar Print</h3>
              </div>
              <div className="card-body py-3">
                <div className="row g-2">
                  {[
                    ["lineHeight", "Detail Line Height", 0.5, 3, 0.1],
                    ["charsPerLine", "Characters Per Line", 28, 42, 1],
                    ["characterSpacing", "Character Spacing", 0, 4, 1],
                  ].map(([key, label, min, max, step]) => (
                    <div className="col-md-4" key={key}>
                      <label className="form-label d-flex justify-content-between">
                        <span>{label}</span>
                        <strong>{settings[key]}</strong>
                      </label>
                      <input
                        type="range"
                        className="form-range"
                        min={min}
                        max={max}
                        step={step}
                        value={settings[key]}
                        onChange={(event) =>
                          setSettings((current) => ({
                            ...current,
                            [key]: Number(event.target.value),
                          }))
                        }
                      />
                    </div>
                  ))}
                  {PRINTER_SIZE_FIELDS.map(({ key, label, hint }) => (
                    <div className="col-md-4" key={key}>
                      <label className="form-label" htmlFor={`printer-size-${key}`}>{label}</label>
                      <select
                        id={`printer-size-${key}`}
                        className="form-select"
                        value={toPrinterSizeValue(settings[key])}
                        onChange={(event) =>
                          setSettings((current) => ({
                            ...current,
                            [key]: Number(event.target.value),
                          }))
                        }
                      >
                        {PRINTER_SIZE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <div className="text-muted small mt-1">{hint}</div>
                    </div>
                  ))}
                  <div className="col-md-4">
                    <label className="form-label" htmlFor="paper-width">Paper Width (mm)</label>
                    <select
                      id="paper-width"
                      className="form-select"
                      value={settings.paperWidth}
                      onChange={(event) => setSettings((current) => ({
                        ...current,
                        paperWidth: Number(event.target.value),
                      }))}
                    >
                      <option value={58}>58 mm</option>
                      <option value={80}>80 mm</option>
                    </select>
                    {wrapperPaperWidth && (
                      <div className={`small mt-1 ${wrapperPaperWidth !== settings.paperWidth ? "text-warning" : "text-muted"}`}>
                        Printer di APK: {wrapperPaperWidth} mm.
                        {wrapperPaperWidth !== settings.paperWidth && " Cetak memakai ukuran dari APK."}
                      </div>
                    )}
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Printer Font</label>
                    <select
                      className="form-select"
                      value={settings.printerFont}
                      onChange={(event) =>
                        setSettings((current) => ({
                          ...current,
                          printerFont: event.target.value,
                        }))
                      }
                    >
                      <option value="font_a">FontA - normal</option>
                      <option value="font_b">FontB - kecil/padat</option>
                      <option value="font_c">FontC - lebih tebal/terbaca</option>
                    </select>
                    <div className="text-muted small mt-1">
                      Jika hasil terlalu pipih, pakai Font A dan kurangi Characters Per Line.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-header py-2 d-flex justify-content-between align-items-center">
                <div>
                  <h3 className="card-title mb-0">Detail Booking</h3>
                  <div className="text-muted small">
                    Sesuaikan sedikit urutan, bold, alignment, dan font detail reservasi.
                  </div>
                </div>
                <span className="badge bg-blue-lt text-blue">{visibleFields.length} visible</span>
              </div>
              <div className="table-responsive">
                <table className="table table-vcenter table-sm mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: 95 }}>Order</th>
                      <th>Field</th>
                      <th style={{ width: 105 }}>Visible</th>
                      <th style={{ width: 95 }}>Bold</th>
                      <th style={{ width: 170 }}>Align</th>
                      <th style={{ width: 140 }}>Font</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fields.map((field, index) => {
                      const AlignIcon = alignIcon[field.align] || IconAlignLeft;
                      return (
                        <tr key={field.id}>
                          <td>
                            <div className="btn-group btn-group-sm">
                              <button
                                className="btn btn-outline-secondary"
                                disabled={index === 0}
                                onClick={() => setFields((current) => moveItem(current, index, "up"))}
                              >
                                <IconArrowUp size={14} />
                              </button>
                              <button
                                className="btn btn-outline-secondary"
                                disabled={index === fields.length - 1}
                                onClick={() => setFields((current) => moveItem(current, index, "down"))}
                              >
                                <IconArrowDown size={14} />
                              </button>
                            </div>
                          </td>
                          <td>
                            <input
                              className="form-control form-control-sm fw-bold mb-1"
                              value={field.label}
                              onChange={(event) => updateField(field.id, { label: event.target.value })}
                            />
                            <input
                              className="form-control form-control-sm"
                              value={field.value}
                              onChange={(event) => updateField(field.id, { value: event.target.value })}
                            />
                          </td>
                          <td>
                            <label className="form-check form-switch m-0">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={field.visible}
                                onChange={(event) => updateField(field.id, { visible: event.target.checked })}
                              />
                            </label>
                          </td>
                          <td>
                            <button
                              className={`btn btn-sm ${field.bold ? "btn-dark" : "btn-outline-secondary"}`}
                              onClick={() => updateField(field.id, { bold: !field.bold })}
                            >
                              <IconBold size={15} />
                            </button>
                          </td>
                          <td>
                            <div className="btn-group btn-group-sm">
                              {["left", "center", "right"].map((align) => {
                                const Icon = alignIcon[align];
                                return (
                                  <button
                                    key={align}
                                    className={`btn ${field.align === align ? "btn-primary" : "btn-outline-primary"}`}
                                    onClick={() => updateField(field.id, { align })}
                                  >
                                    <Icon size={14} />
                                  </button>
                                );
                              })}
                            </div>
                            <div className="text-muted small mt-1">
                              <AlignIcon size={13} /> {field.align}
                            </div>
                          </td>
                          <td>
                            <input
                              type="number"
                              min={6}
                              max={24}
                              className="form-control form-control-sm"
                              value={field.fontSize}
                              onChange={(event) => updateField(field.id, { fontSize: Number(event.target.value) })}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="card mt-3">
              <div className="card-header py-2 d-flex justify-content-between align-items-center">
                <div>
                  <h3 className="card-title mb-0">Activity, Meal & Payment</h3>
                  <div className="text-muted small">
                    Adjustment tipis untuk baris item, qty, meal voucher, dan total paid.
                  </div>
                </div>
                <span className="badge bg-secondary-lt text-secondary">Current print blocks</span>
              </div>
              <div className="table-responsive">
                <table className="table table-vcenter table-sm mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: 95 }}>Order</th>
                      <th>Element</th>
                      <th style={{ width: 105 }}>Visible</th>
                      <th style={{ width: 95 }}>Bold</th>
                      <th style={{ width: 170 }}>Align</th>
                      <th style={{ width: 140 }}>Font</th>
                    </tr>
                  </thead>
                  <tbody>
                    {itemStyles.map((item, index) => (
                      <tr key={item.id}>
                        <td>
                          <div className="btn-group btn-group-sm">
                            <button
                              className="btn btn-outline-secondary"
                              disabled={index === 0}
                              onClick={() => setItemStyles((current) => moveItem(current, index, "up"))}
                            >
                              <IconArrowUp size={14} />
                            </button>
                            <button
                              className="btn btn-outline-secondary"
                              disabled={index === itemStyles.length - 1}
                              onClick={() => setItemStyles((current) => moveItem(current, index, "down"))}
                            >
                              <IconArrowDown size={14} />
                            </button>
                          </div>
                        </td>
                        <td>
                          <input
                            className="form-control form-control-sm fw-bold mb-1"
                            value={item.label}
                            onChange={(event) => updateItemStyle(item.id, { label: event.target.value })}
                          />
                          <input
                            className="form-control form-control-sm"
                            value={item.sample}
                            onChange={(event) => updateItemStyle(item.id, { sample: event.target.value })}
                          />
                        </td>
                        <td>
                          <label className="form-check form-switch m-0">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={item.visible}
                              onChange={(event) => updateItemStyle(item.id, { visible: event.target.checked })}
                            />
                          </label>
                        </td>
                        <td>
                          <button
                            className={`btn btn-sm ${item.bold ? "btn-dark" : "btn-outline-secondary"}`}
                            onClick={() => updateItemStyle(item.id, { bold: !item.bold })}
                          >
                            <IconBold size={15} />
                          </button>
                        </td>
                        <td>
                          <div className="btn-group btn-group-sm">
                            {["left", "center", "right"].map((align) => {
                              const Icon = alignIcon[align];
                              return (
                                <button
                                  key={align}
                                  className={`btn ${item.align === align ? "btn-primary" : "btn-outline-primary"}`}
                                  onClick={() => updateItemStyle(item.id, { align })}
                                >
                                  <Icon size={14} />
                                </button>
                              );
                            })}
                          </div>
                        </td>
                        <td>
                          <input
                            type="number"
                            min={6}
                            max={28}
                            className="form-control form-control-sm"
                            value={item.fontSize}
                            onChange={(event) => updateItemStyle(item.id, { fontSize: Number(event.target.value) })}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="col-xl-4">
            <div className="card sticky-top" style={{ top: 16 }}>
              <div className="card-header py-2">
                <h3 className="card-title mb-0">
                  <IconPrinter size={18} className="me-1" />
                  Preview Desain Aktif
                </h3>
              </div>
              <div className="card-body bg-light">
                <div
                  className="mx-auto bg-white shadow-sm"
                  style={{
                    width: `${settings.paperWidth}mm`,
                    minHeight: 520,
                    padding: 10,
                    color: "#111",
                    fontFamily: PREVIEW_FONT_FAMILIES[settings.printerFont] || PREVIEW_FONT_FAMILIES.font_a,
                    letterSpacing: `${settings.characterSpacing * 0.35}px`,
                  }}
                >
                  <div className="text-center" style={{ ...FIXED_PREVIEW_TEXT, fontSize: toPrinterSizeValue(settings.titleFontSize), fontWeight: 700 }}>
                    {printType === "invoice" ? "GUEST INVOICE" : printType === "meal" ? "RESTO" : "CUSTOMER"}
                  </div>
                  <div className="text-center my-1">
                    <img src={logoPreview} alt="Print logo preview" style={{ width: 82, height: "auto" }} />
                  </div>
                  <div className="text-center" style={FIXED_PREVIEW_TEXT}>
                    <div style={{ fontWeight: 700, fontSize: toPrinterSizeValue(settings.headerFontSize) }}>{companyName}</div>
                    <div style={{ fontSize: toPrinterSizeValue(settings.companyFontSize) }}>{companyAddress}</div>
                  </div>
                  <div className="border-top border-dark border-dashed my-2" />
                  {printType !== "meal" && (
                    <div className="text-center fw-bold mb-1" style={{ ...FIXED_PREVIEW_TEXT, fontSize: toPrinterSizeValue(settings.titleFontSize) }}>
                      TICKET
                    </div>
                  )}
                  <div className="my-2">
                    {visibleFields.map((field) => (
                      <div
                        key={field.id}
                        className="d-flex justify-content-between"
                        style={{
                          gap: 8,
                          marginBottom: 0,
                          lineHeight: settings.lineHeight,
                          fontSize: field.fontSize,
                          fontWeight: field.bold ? 700 : 400,
                        }}
                      >
                        <span>{field.label}:</span>
                        <span className="flex-fill" style={{ textAlign: field.align }}>
                          {field.value}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="border-top border-dark my-2" />
                  {printType !== "meal" && (
                    <>
                      <div className="text-center fw-bold mb-1" style={{ fontSize: 13 }}>
                        Main Activities
                      </div>
                      {itemStyleMap.itemHeader?.visible && (
                        <div
                          style={{
                            fontSize: itemStyleMap.itemHeader.fontSize,
                            fontWeight: itemStyleMap.itemHeader.bold ? 700 : 400,
                            textAlign: itemStyleMap.itemHeader.align,
                          }}
                        >
                          {itemStyleMap.itemHeader.sample}
                        </div>
                      )}
                      <div className="d-flex justify-content-between">
                        {["itemName", "itemQty"].map((id) => {
                          const item = itemStyleMap[id];
                          if (!item?.visible) return null;
                          return (
                            <span
                              key={id}
                              style={{
                                flex: id === "itemName" ? 1 : "0 0 32px",
                                fontSize: item.fontSize,
                                fontWeight: item.bold ? 700 : 400,
                                textAlign: item.align,
                              }}
                            >
                              {id === "itemName" ? "ATV Single" : id === "itemQty" ? "2" : item.sample}
                            </span>
                          );
                        })}
                      </div>
                      <div className="border-top border-dark my-2" />
                      <div className="text-center fw-bold" style={{ ...FIXED_PREVIEW_TEXT, fontSize: toPrinterSizeValue(settings.titleFontSize) }}>
                        PREMIUM
                      </div>
                    </>
                  )}
                  {(printType === "meal" || printType === "ticket") && (
                    <>
                      <div className="border-top border-dark my-2" />
                      <div className="text-center fw-bold mb-1" style={{ ...FIXED_PREVIEW_TEXT, fontSize: toPrinterSizeValue(settings.titleFontSize) }}>
                        Meals Voucher
                      </div>
                      <div className="d-flex justify-content-between border-bottom pb-1 mb-1">
                        {["mealName", "mealQty"].map((id) => {
                          const item = itemStyleMap[id];
                          if (!item?.visible) return null;
                          return (
                            <span
                              key={id}
                              style={{
                                flex: id === "mealName" ? 1 : "0 0 32px",
                                fontSize: item.fontSize,
                                fontWeight: item.bold ? 700 : 400,
                                textAlign: item.align,
                              }}
                            >
                              {id === "mealName" ? "Lunch Package" : id === "mealQty" ? "2" : item.sample}
                            </span>
                          );
                        })}
                      </div>
                    </>
                  )}
                  {printType === "invoice" && (
                    <div className="text-center fw-bold" style={{ ...FIXED_PREVIEW_TEXT, fontSize: toPrinterSizeValue(settings.titleFontSize) }}>
                      PREMIUM
                    </div>
                  )}
                  <div className="border-top border-dark my-2" />
                  {itemStyleMap.totalPaid?.visible && (
                    <div
                      className="d-flex justify-content-between"
                      style={{
                        fontSize: itemStyleMap.totalPaid.fontSize,
                        fontWeight: itemStyleMap.totalPaid.bold ? 700 : 400,
                      }}
                    >
                      <span>Total Paid:</span>
                      <span
                        className="flex-fill"
                        style={{ textAlign: itemStyleMap.totalPaid.align }}
                      >
                        {itemStyleMap.totalPaid.sample}
                      </span>
                    </div>
                  )}
                  <div className="border-top border-dark my-3" />
                  <div
                    className="text-center"
                    style={{
                      ...FIXED_PREVIEW_TEXT,
                      fontSize: toPrinterSizeValue(settings.footerFontSize),
                      fontWeight: 700,
                    }}
                  >
                    {footerText}
                  </div>
                </div>
              </div>
              <div className="card-footer text-muted small">
                Setting ini tersimpan lokal per device/browser. Untuk seragam di semua tablet, perlu endpoint API print settings.
              </div>
            </div>
          </div>
        </div>
      </div>
    </LayoutDashboard>
  );
};

export default PrintLayoutSettingsPage;
